import { db, type LocalFile } from '@/offline/db'
import { env } from './env'
import { supabase } from './supabase'

export const BUCKET = 'project-files'
/** Supabase Free plan limit per file. Larger files go to Cam another way for now (see DECISIONS.md). */
export const MAX_UPLOAD_BYTES = 50 * 1024 * 1024
const TUS_THRESHOLD = 6 * 1024 * 1024
const MAX_EDGE = 2000

export function formatBytes(n: number) {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`
  return `${(n / 1024 / 1024).toFixed(1)} MB`
}

export function safeName(name: string) {
  const cleaned = name
    .normalize('NFKD')
    .replace(/[^\w.\- ]+/g, '')
    .replace(/\s+/g, '-')
    .slice(-80)
  return cleaned || 'file'
}

/**
 * Shrink a photo before it is stored: longest edge 2000 px, JPEG at 80%. Re-encoding through a
 * canvas also strips EXIF, including GPS location. Falls back to the original if decoding fails.
 */
export async function compressImage(file: Blob): Promise<Blob> {
  if (!file.type.startsWith('image/') || file.type === 'image/gif' || file.type === 'image/svg+xml')
    return file
  try {
    const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' })
    const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height))
    const w = Math.round(bitmap.width * scale)
    const h = Math.round(bitmap.height * scale)
    const canvas = document.createElement('canvas')
    canvas.width = w
    canvas.height = h
    canvas.getContext('2d')!.drawImage(bitmap, 0, 0, w, h)
    bitmap.close()
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', 0.8),
    )
    return blob && blob.size < file.size ? blob : file
  } catch {
    return file
  }
}

/** Keep a file on the device (IndexedDB) until it has uploaded. */
export async function saveLocalFile(
  userId: string,
  blob: Blob,
  name: string,
  extra: Partial<LocalFile> = {},
): Promise<string> {
  const id = crypto.randomUUID()
  await db.files.add({
    id,
    userId,
    name,
    type: blob.type || 'application/octet-stream',
    size: blob.size,
    blob,
    createdAt: Date.now(),
    ...extra,
  })
  return id
}

export async function deleteLocalFiles(ids: string[]) {
  await db.files.bulkDelete(ids)
}

/**
 * Upload to Storage. Files over 6 MB use resumable (TUS) uploads, so a dropped connection resumes
 * where it stopped. Re-uploading the same path is safe (upsert).
 */
export async function uploadToStorage(
  path: string,
  blob: Blob,
  contentType: string,
  onProgress?: (fraction: number) => void,
) {
  if (blob.size > MAX_UPLOAD_BYTES) {
    throw new Error(
      `Too big to upload (${formatBytes(blob.size)}). The limit is ${formatBytes(MAX_UPLOAD_BYTES)}.`,
    )
  }
  if (blob.size <= TUS_THRESHOLD) {
    const { error } = await supabase.storage
      .from(BUCKET)
      .upload(path, blob, { upsert: true, contentType })
    if (error) throw new Error(error.message)
    onProgress?.(1)
    return
  }
  const { Upload } = await import('tus-js-client')
  const { data } = await supabase.auth.getSession()
  const token = data.session?.access_token
  if (!token) throw new Error('Not signed in')
  await new Promise<void>((resolve, reject) => {
    const upload = new Upload(blob, {
      endpoint: `${env.supabaseUrl}/storage/v1/upload/resumable`,
      retryDelays: [0, 3000, 5000, 10000, 20000],
      headers: { authorization: `Bearer ${token}`, 'x-upsert': 'true' },
      uploadDataDuringCreation: true,
      removeFingerprintOnSuccess: true,
      chunkSize: TUS_THRESHOLD,
      metadata: { bucketName: BUCKET, objectName: path, contentType, cacheControl: '3600' },
      onError: reject,
      onProgress: (sent, total) => onProgress?.(total ? sent / total : 0),
      onSuccess: () => resolve(),
    })
    void upload.findPreviousUploads().then((previous) => {
      if (previous[0]) upload.resumeFromPreviousUpload(previous[0])
      upload.start()
    })
  })
}

/** Short-lived link to view or download a stored file. */
export async function signedUrl(path: string, download?: string) {
  const { data, error } = await supabase.storage
    .from(BUCKET)
    .createSignedUrl(path, 60 * 60, download ? { download } : undefined)
  if (error) throw new Error(error.message)
  return data.signedUrl
}
